import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { __ } from '@wordpress/i18n';
import SbUtils from '../../Utils/SbUtils';
import { hexToHue } from '../../Utils/ColorUtils';
import ReviewAlertEditorContext from '../Context/ReviewAlertEditorContext';

// Number of reviews to show initially in expanded view
const INITIAL_EXPANDED_REVIEW_COUNT = 10;

/**
 * Review Alert Preview Component
 *
 * Renders a live preview of the review alert.
 * Matches prototype design from 2025-11-19-review-notification-popup-v2
 *
 * Structure:
 * - Wrapper with hover scale effect
 * - Inner accent-colored border container
 * - White card with horizontal layout (avatar left, content right)
 * - Powered by footer in accent color
 *
 * Themes:
 * - DEFAULT (Light): White card with accent border
 * - DARK: Dark card with accent border
 * - MINIMAL: Border-only accent outline
 * - MINIMAL-DARK: Dark card with border-only accent outline
 */
const ReviewAlertPreview = ( { settings, reviews, totalReviews: totalReviewsProp, averageRating: averageRatingProp, isLoading } ) => {
    const {
        editorActiveDevice,
        isPreviewExpanded: isExpanded,
        setIsPreviewExpanded: setIsExpanded
    } = useContext( ReviewAlertEditorContext );
    const [ currentReviewIndex, setCurrentReviewIndex ] = useState( 0 );
    const [ isTransitioning, setIsTransitioning ] = useState( false );
    const [ isAnimating, setIsAnimating ] = useState( false ); // Track animation state
    const [ showExpanded, setShowExpanded ] = useState( false ); // What to render
    const [ showAllReviews, setShowAllReviews ] = useState( false ); // Track if all reviews shown in expanded view

    const devices = [ 'desktop', 'tablet', 'mobile' ];

    // Reset review index when reviews change to avoid out of bounds
    useEffect( () => {
        setCurrentReviewIndex( 0 );
    }, [ reviews ] );

    // Handle expand/collapse with animation delay
    useEffect( () => {
        if ( isExpanded && !showExpanded ) {
            // Expanding: immediately show expanded content with entrance animation
            setIsAnimating( true );
            setShowExpanded( true );
            const timer = setTimeout( () => setIsAnimating( false ), 300 );
            return () => clearTimeout( timer );
        } else if ( !isExpanded && showExpanded ) {
            // Collapsing: start exit animation, then switch content
            setIsAnimating( true );
            const timer = setTimeout( () => {
                setShowExpanded( false );
                setShowAllReviews( false ); // Reset "See all" state when collapsing
                setIsAnimating( false );
            }, 300 ); // Match animation duration
            return () => clearTimeout( timer );
        }
    }, [ isExpanded, showExpanded ] );

    // Get current settings with defaults
    // Theme is stored as light/minimal/dark/minimal-dark, CSS handles the styling
    const theme = settings?.theme || 'light';
    const position = settings?.position || 'bottom-right';
    const popupType = settings?.popup_type || 'aggregate'; // 'aggregate' or 'recent'
    const accentColor = settings?.accent_color || '#175CE3';
    // Derive accentHue from accentColor if not explicitly saved
    // This ensures the preview matches the color picker when accent_hue is missing
    const accentHue = settings?.accent_hue || String( hexToHue( accentColor ) );
    const content = settings?.content || {};

    // Content visibility settings
    const showAvatar = content.show_avatar !== false;
    const showRating = content.show_rating !== false;
    const showReviewerName = content.show_reviewer_name !== false;
    const showReviewText = content.show_review_text !== false;
    const showDate = content.show_date !== false;
    const showPlatform = content.show_platform !== false;
    const showTotalReviews = content.show_total_reviews !== false;
    const showPoweredBy = content.show_powered_by !== false;

    // Review Feed (expanded popup) settings
    const reviewFeed = settings?.review_feed || {};
    const showExpandedHeading = reviewFeed.show_heading !== false;
    const expandedHeadingText = reviewFeed.heading_text || __( 'See what our Customers say about Smash Balloon feeds!', 'reviews-feed' );
    const showExpandedButton = reviewFeed.show_button !== false;
    const expandedButtonText = reviewFeed.button_text || __( 'Get Smash Balloon Feed Pro', 'reviews-feed' );
    const expandedButtonUrl = reviewFeed.button_url || '#';
    const expandedButtonIcon = reviewFeed.button_icon || null;
    const showExpandedPoweredBy = reviewFeed.show_powered_by !== false;

    // Review card visibility settings (for expanded popup reviews)
    const showExpandedStars = reviewFeed.show_stars !== false;
    const showExpandedTitle = reviewFeed.show_title !== false;
    const showExpandedContent = reviewFeed.show_content !== false;
    const showExpandedAuthor = reviewFeed.show_author !== false;
    const showExpandedDate = reviewFeed.show_date !== false;

    // Source and review availability checks (for admin preview empty states)
    const hasSources = settings?.sources?.length > 0;
    const hasReviews = reviews?.length > 0;

    // Reviews data - use real reviews from props, memoized to prevent unnecessary re-renders
    const displayReviews = useMemo( () => reviews || [], [ reviews ] );

    // Determine if popup can be expanded (only when there are reviews to show)
    const canExpand = displayReviews.length > 0;

    const currentReview = displayReviews[ currentReviewIndex ];
    // Use total from backend, fallback to reviews array length, or 0 if no reviews
    const totalReviews = totalReviewsProp > 0 ? totalReviewsProp : ( reviews?.length || displayReviews.length );

    // Cycle through reviews with fade transition (only for 'recent' type)
    useEffect( () => {
        // Don't cycle for aggregate type, if expanded, or if only 1 review
        if ( popupType === 'aggregate' || displayReviews.length <= 1 || isExpanded ) {
            return;
        }

        const cycleInterval = settings?.timing?.cycle_interval_max || 8000;
        // Use ref-like object to track timeouts for proper cleanup
        const timeoutRefs = { content: null, animation: null };

        const interval = setInterval( () => {
            setIsTransitioning( true );
            // Change content at 200ms (when elements are invisible - 40% of 500ms animation)
            timeoutRefs.content = setTimeout( () => {
                setCurrentReviewIndex( ( prev ) => ( prev + 1 ) % displayReviews.length );
            }, 200 );
            // Remove cycling class after full animation completes (500ms)
            timeoutRefs.animation = setTimeout( () => {
                setIsTransitioning( false );
            }, 500 );
        }, cycleInterval );

        return () => {
            clearInterval( interval );
            if ( timeoutRefs.content ) {
                clearTimeout( timeoutRefs.content );
            }
            if ( timeoutRefs.animation ) {
                clearTimeout( timeoutRefs.animation );
            }
        };
    }, [ popupType, displayReviews.length, settings?.timing?.cycle_interval_max, isExpanded ] );

    // Build CSS classes
    const themeClass = `sbr-review-alert--${theme}`;
    const positionClass = `sbr-review-alert--${position}`;
    const poweredByClass = showPoweredBy ? 'sbr-review-alert--has-powered-by' : '';
    const popupTypeClass = popupType === 'aggregate' ? 'sbr-review-alert--aggregate' : '';

    // Custom accent color style (OKLCH-based theming)
    // Hue value 0-360 matches standard color wheel
    const customStyle = {
        '--sbr-popup-accent-hue': accentHue,
        '--sbr-popup-accent-fallback': accentColor
    };

    // Render stars - memoized to avoid recreating on every render
    // Note: Size controlled by CSS variable --sbr-popup-star-size for responsive behavior
    const renderStars = useCallback( ( rating = 5 ) => {
        const stars = [];
        for ( let i = 1; i <= 5; i++ ) {
            const isEmpty = i > rating;
            stars.push(
                <span
                    key={ i }
                    className={ `sbr-review-alert__star ${ isEmpty ? 'sbr-review-alert__star--empty' : '' }` }
                >
                    { SbUtils.printIcon( 'popup-star' ) }
                </span>
            );
        }
        return stars;
    }, [] );

    // Render chevron icon - memoized to avoid recreating on every render
    const renderChevron = useCallback( () => (
        <span className="sbr-review-alert__chevron">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
        </span>
    ), [] );

    // Render close icon - memoized to avoid recreating on every render
    const renderCloseIcon = useCallback( () => (
        <span className="sbr-review-alert__close-icon">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={ 2 } d="M6 18L18 6M6 6l12 12" />
            </svg>
        </span>
    ), [] );

    // Render expanded stars - memoized to avoid recreating on every render
    // Note: Size controlled by CSS variable --sbr-popup-star-size for responsive behavior
    const renderExpandedStars = useCallback( ( rating = 5 ) => {
        const stars = [];
        for ( let i = 1; i <= 5; i++ ) {
            const isEmpty = i > rating;
            stars.push(
                <span
                    key={ i }
                    className={ `sbr-review-alert__expanded-star ${ isEmpty ? 'sbr-review-alert__expanded-star--empty' : '' }` }
                >
                    { SbUtils.printIcon( 'popup-star' ) }
                </span>
            );
        }
        return stars;
    }, [] );

    // Get provider icon name - memoized to avoid recreating on every render
    const getProviderIcon = useCallback( ( provider ) => {
        const providers = {
            'facebook': 'facebook-provider',
            'google': 'google-provider',
            'yelp': 'yelp-provider',
            'tripadvisor': 'tripadvisor-provider',
            'edd': 'edd-provider',
            'woocommerce': 'woocommerce-provider',
            'wordpress.org': 'wordpress.org-provider',
            'trustpilot': 'trustpilot-provider',
            'airbnb': 'airbnb-provider',
            'aliexpress': 'aliexpress-provider',
            'booking': 'booking-provider',
            'zomato': 'zomato-provider',
            'wpforms': 'wpforms-provider',
            'formidable': 'formidable-provider'
        };
        // Return the mapped provider or construct default pattern
        return providers[ provider ] || ( provider ? `${ provider }-provider` : 'google-provider' );
    }, [] );

    // Render button icon for expanded popup CTA - memoized to avoid recreating on every render
    const renderButtonIcon = useCallback( ( iconId ) => {
        if ( !iconId ) return null;

        const icons = {
            'arrow-right': (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="sbr-review-alert__expanded-cta-icon">
                    <path fillRule="evenodd" d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z" clipRule="evenodd" />
                </svg>
            ),
            'external-link': (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="sbr-review-alert__expanded-cta-icon">
                    <path d="M6.22 8.72a.75.75 0 0 0 1.06 1.06l5.22-5.22v1.69a.75.75 0 0 0 1.5 0v-3.5a.75.75 0 0 0-.75-.75h-3.5a.75.75 0 0 0 0 1.5h1.69L6.22 8.72Z" />
                    <path d="M3.5 6.75c0-.69.56-1.25 1.25-1.25H7A.75.75 0 0 0 7 4H4.75A2.75 2.75 0 0 0 2 6.75v4.5A2.75 2.75 0 0 0 4.75 14h4.5A2.75 2.75 0 0 0 12 11.25V9a.75.75 0 0 0-1.5 0v2.25c0 .69-.56 1.25-1.25 1.25h-4.5c-.69 0-1.25-.56-1.25-1.25v-4.5Z" />
                </svg>
            ),
            'chevron-right': (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="sbr-review-alert__expanded-cta-icon">
                    <path fillRule="evenodd" d="M6.22 4.22a.75.75 0 0 1 1.06 0l3.25 3.25a.75.75 0 0 1 0 1.06l-3.25 3.25a.75.75 0 0 1-1.06-1.06L8.94 8 6.22 5.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                </svg>
            ),
            'star': (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="sbr-review-alert__expanded-cta-icon">
                    <path fillRule="evenodd" d="M8 1.75a.75.75 0 0 1 .692.462l1.41 3.393 3.664.293a.75.75 0 0 1 .428 1.317l-2.791 2.39.853 3.575a.75.75 0 0 1-1.12.814L7.998 12.08l-3.135 1.915a.75.75 0 0 1-1.12-.814l.852-3.574-2.79-2.39a.75.75 0 0 1 .427-1.318l3.663-.293 1.41-3.393A.75.75 0 0 1 8 1.75Z" clipRule="evenodd" />
                </svg>
            ),
            'heart': (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="sbr-review-alert__expanded-cta-icon">
                    <path d="M2 6.342a3.375 3.375 0 0 1 6-2.088 3.375 3.375 0 0 1 5.997 2.26c-.063 2.134-1.618 3.76-2.955 4.784a14.437 14.437 0 0 1-2.676 1.61c-.02.01-.038.017-.053.022l-.018.007-.004.002h-.002a.75.75 0 0 1-.592 0h-.001l-.005-.002-.018-.007a5.386 5.386 0 0 1-.053-.022 14.437 14.437 0 0 1-2.676-1.61C3.618 10.102 2.063 8.476 2 6.342Z" />
                </svg>
            )
        };

        return icons[ iconId ] || null;
    }, [] );

    // Handle "See all" button click - memoized to ensure consistent behavior in production builds
    // Uses event delegation pattern to handle clicks on button or its children (span, chevron)
    const handleSeeAllClick = useCallback( ( e ) => {
        e.preventDefault();
        e.stopPropagation();
        setShowAllReviews( true );
    }, [] );

    // Handle clicks on expanded reviews container - event delegation for "See all" button
    // This ensures clicks on child elements (span text, chevron SVG) are properly handled
    const handleExpandedReviewsClick = useCallback( ( e ) => {
        // Check if click was on "See all" button or its children
        const seeAllBtn = e.target.closest( '.sbr-review-alert__expanded-see-all-btn' );
        if ( seeAllBtn ) {
            e.preventDefault();
            e.stopPropagation();
            setShowAllReviews( true );
        }
    }, [] );

    // Use backend-provided average rating (calculated from ALL reviews), or fallback to client calculation
    const averageRating = useMemo( () => {
        // Prefer backend-provided rating (calculated from all matching reviews, not just displayed 10)
        if ( averageRatingProp !== null && averageRatingProp !== undefined ) {
            return parseFloat( averageRatingProp ).toFixed( 1 );
        }
        // Fallback to client-side calculation for demo mode
        if ( !displayReviews || displayReviews.length === 0 ) return '5.0';
        const sum = displayReviews.reduce( ( acc, review ) => acc + ( review.rating || 5 ), 0 );
        return ( sum / displayReviews.length ).toFixed( 1 );
    }, [ averageRatingProp, displayReviews ] );

    // Render loading skeleton placeholder while reviews are loading
    const renderLoadingSkeleton = () => (
        <div className="sbr-review-alert__wrapper">
            <div className="sbr-review-alert__inner">
                <div className="sbr-review-alert__card">
                    <div className="sbr-review-alert__content-row">
                        { /* Avatar skeleton */ }
                        <div className="sbr-popup-skeleton sbr-popup-skeleton--avatar"></div>
                        { /* Content skeleton */ }
                        <div className="sbr-review-alert__content">
                            { /* Stars skeleton */ }
                            <div className="sbr-popup-skeleton sbr-popup-skeleton--stars"></div>
                            { /* Heading skeleton */ }
                            <div className="sbr-popup-skeleton sbr-popup-skeleton--heading"></div>
                            { /* Text skeleton */ }
                            <div className="sbr-popup-skeleton sbr-popup-skeleton--text"></div>
                            { /* Link skeleton */ }
                            <div className="sbr-popup-skeleton sbr-popup-skeleton--link"></div>
                        </div>
                    </div>
                </div>
                { /* Powered by skeleton */ }
                { showPoweredBy && (
                    <div className="sbr-review-alert__powered-by">
                        <div className="sbr-popup-skeleton sbr-popup-skeleton--powered-by"></div>
                    </div>
                ) }
            </div>
        </div>
    );

    // Empty state icon SVG (info circle)
    const renderEmptyIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
    );

    // Render empty state for collapsed popup (no sources or no reviews)
    const renderEmptyState = () => {
        const message = hasSources
            ? __( 'No reviews for selected sources', 'reviews-feed' )
            : __( 'No sources added yet', 'reviews-feed' );

        return (
            <div className="sbr-review-alert__wrapper">
                <div className="sbr-review-alert__inner">
                    <div className="sbr-review-alert__card">
                        <div className="sbr-review-alert__empty-state">
                            <div className="sbr-review-alert__empty-icon">
                                { renderEmptyIcon() }
                            </div>
                            <p className="sbr-review-alert__empty-text">{ message }</p>
                        </div>
                    </div>
                    { showPoweredBy && (
                        <div className="sbr-review-alert__powered-by">
                            <span className="sbr-review-alert__powered-text">
                                { __( 'Powered by', 'reviews-feed' ) }
                            </span>
                            <a
                                href="https://smashballoon.com/reviews-feed/?utm_source=reviews-popup&utm_medium=footer&utm_campaign=powered-by"
                                className="sbr-review-alert__powered-link"
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                            >
                                { SbUtils.printIcon( 'smashballoon-logo-white', 'sbr-review-alert__logo' ) }
                            </a>
                        </div>
                    ) }
                </div>
            </div>
        );
    };

    // Render empty state for expanded popup
    const renderExpandedEmptyState = () => {
        const message = hasSources
            ? __( 'No reviews for selected sources', 'reviews-feed' )
            : __( 'No sources added yet', 'reviews-feed' );

        return (
            <div className="sbr-review-alert__expanded">
                <div className="sbr-review-alert__expanded-inner">
                    {/* Header */}
                    { showExpandedHeading && (
                        <div className="sbr-review-alert__expanded-header">
                            <h3 className="sbr-review-alert__expanded-heading">{ expandedHeadingText }</h3>
                        </div>
                    ) }

                    {/* Empty State */}
                    <div className={ `sbr-review-alert__expanded-reviews${ !showExpandedHeading && !showExpandedButton ? ' sbr-review-alert__expanded-reviews--no-header' : '' }` }>
                        <div className="sbr-review-alert__expanded-empty-state">
                            <div className="sbr-review-alert__empty-icon sbr-review-alert__empty-icon--large">
                                { renderEmptyIcon() }
                            </div>
                            <p className="sbr-review-alert__empty-text">{ message }</p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="sbr-review-alert__expanded-footer">
                        { showExpandedButton && (
                            <a
                                href={ expandedButtonUrl }
                                className="sbr-review-alert__expanded-cta"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                { expandedButtonIcon && (
                                    <span className="sbr-review-alert__expanded-cta-icon">
                                        { renderButtonIcon( expandedButtonIcon ) }
                                    </span>
                                ) }
                                <span>{ expandedButtonText }</span>
                                <span className="sbr-review-alert__chevron">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </span>
                            </a>
                        ) }
                    </div>
                </div>

                {/* Powered By */}
                { showExpandedPoweredBy && (
                    <div className="sbr-review-alert__expanded-powered-wrapper">
                        <a
                            href="https://smashballoon.com/reviews-feed/?utm_source=reviews-popup&utm_medium=footer&utm_campaign=powered-by"
                            className="sbr-review-alert__expanded-powered-pill"
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                        >
                            <span className="sbr-review-alert__expanded-powered-text">
                                { __( 'Powered by', 'reviews-feed' ) }
                            </span>
                            <span className="sbr-review-alert__expanded-powered-logo">
                                { SbUtils.printIcon( 'smashballoon-logo', 'sbr-review-alert__logo' ) }
                            </span>
                        </a>
                    </div>
                ) }

                {/* Close Button */}
                <div className="sbr-review-alert__close-row">
                    <button
                        className="sbr-review-alert__close-btn"
                        onClick={ () => setIsExpanded( false ) }
                        aria-label={ __( 'Close', 'reviews-feed' ) }
                    >
                        { renderCloseIcon() }
                    </button>
                </div>
            </div>
        );
    };

    // Render aggregate content (summary view - no individual reviews)
    // Reuses element structure from recent reviews template for consistent styling
    const renderAggregateContent = () => (
        <>
            {/* Wrapper - Hover scale effect container */}
            <div className="sbr-review-alert__wrapper">
                {/* Inner - Accent colored border */}
                <div className="sbr-review-alert__inner">
                    {/* Card - White content container */}
                    <div className="sbr-review-alert__card">
                        {/* Content Row - Horizontal Layout for Aggregate */}
                        <div className="sbr-review-alert__content-row sbr-review-alert__content-row--aggregate">
                            {/* Rating Number (replaces avatar) */}
                            { showRating && (
                                <div className="sbr-review-alert__rating-number">
                                    <span className="sbr-review-alert__rating-value">{ averageRating }</span>
                                </div>
                            ) }

                            {/* Content Section - Reusing __content class from recent reviews */}
                            <div className={ `sbr-review-alert__content${ !showRating ? ' sbr-review-alert__content--no-rating' : '' }` }>
                                {/* Stars */}
                                { showRating && (
                                    <div className="sbr-review-alert__stars">
                                        { renderStars( Math.round( parseFloat( averageRating ) ) ) }
                                    </div>
                                ) }

                                {/* View All Link - Reusing hover effect from recent reviews */}
                                { showTotalReviews && (
                                    <a
                                        href="#"
                                        className="sbr-review-alert__view-all-link"
                                        onClick={ ( e ) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if ( !isAnimating ) {
                                                setIsExpanded( true );
                                            }
                                        } }
                                    >
                                        <span>
                                            { totalReviews } { __( 'Total Reviews', 'reviews-feed' ) }
                                        </span>
                                        { renderChevron() }
                                    </a>
                                ) }
                            </div>
                        </div>
                    </div>

                    {/* Powered By Footer */}
                    { showPoweredBy && (
                        <div className="sbr-review-alert__powered-by">
                            <span className="sbr-review-alert__powered-text">
                                { __( 'Powered by', 'reviews-feed' ) }
                            </span>
                            <a
                                href="https://smashballoon.com/reviews-feed/?utm_source=reviews-popup&utm_medium=footer&utm_campaign=powered-by"
                                className="sbr-review-alert__powered-link"
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                            >
                                { SbUtils.printIcon( 'smashballoon-logo-white', 'sbr-review-alert__logo' ) }
                            </a>
                        </div>
                    ) }
                </div>
            </div>
        </>
    );

    // Render the popup content (recent reviews - cycles through individual reviews)
    const renderPopupContent = () => (
        <>
            {/* Wrapper - Hover scale effect container */}
            <div className="sbr-review-alert__wrapper">
                {/* Inner - Accent colored border */}
                <div className={ `sbr-review-alert__inner ${ isTransitioning ? 'sbr-review-alert__inner--transitioning' : '' }` }>
                    {/* Card - White content container */}
                    <div className={ `sbr-review-alert__card sbr-review-alert__review${ isTransitioning ? ' sbr-review-alert__review--cycling' : '' }` }>
                        {/* Date - Top Right */}
                        { showDate && currentReview?.relativeDate && (
                            <span className="sbr-review-alert__date">
                                { currentReview.relativeDate }
                            </span>
                        ) }

                        {/* Content Row - Horizontal Layout */}
                        <div className="sbr-review-alert__content-row">
                            {/* Avatar with Provider Badge */}
                            { showAvatar && (
                                <div className="sbr-review-alert__avatar-wrapper">
                                    <img
                                        className="sbr-review-alert__avatar"
                                        src={ currentReview?.reviewer?.avatar || '' }
                                        alt={ SbUtils.decodeHtmlEntities( currentReview?.reviewer?.name ) || __( 'Reviewer', 'reviews-feed' ) }
                                        onError={ ( e ) => {
                                            e.target.src = 'data:image/svg+xml,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 56 56%22><rect fill=%22%23e4e4e7%22 width=%2256%22 height=%2256%22/><text x=%2250%%22 y=%2250%%22 dominant-baseline=%22middle%22 text-anchor=%22middle%22 font-size=%2224%22 fill=%22%2371717a%22>?</text></svg>';
                                        } }
                                    />
                                    { showPlatform && (
                                        <span
                                            className="sbr-review-alert__provider-badge"
                                            data-provider={ currentReview?.provider || '' }
                                        >
                                            { SbUtils.printIcon( getProviderIcon( currentReview?.provider ), '', null, 24 ) }
                                        </span>
                                    ) }
                                </div>
                            ) }

                            {/* Content Section */}
                            <div className={ `sbr-review-alert__content${ !showRating ? ' sbr-review-alert__content--no-rating' : '' }` }>
                                {/* Stars */}
                                { showRating && (
                                    <div className="sbr-review-alert__stars">
                                        { renderStars( currentReview?.rating || 5 ) }
                                    </div>
                                ) }

                                {/* Reviewer Name - "[Name] left us a review" */}
                                { showReviewerName && (
                                    <div className="sbr-review-alert__heading">
                                        { SbUtils.decodeHtmlEntities( currentReview?.reviewer?.name ) || __( 'Someone', 'reviews-feed' ) } { __( 'left us a review', 'reviews-feed' ) }
                                    </div>
                                ) }

                                {/* Review Text */}
                                { showReviewText && currentReview?.text && (
                                    <p className="sbr-review-alert__review-text">
                                        { SbUtils.decodeHtmlEntities( currentReview.text ) }
                                    </p>
                                ) }

                                {/* View All Link */}
                                { showTotalReviews && (
                                    <a
                                        href="#"
                                        className="sbr-review-alert__view-all-link"
                                        onClick={ ( e ) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if ( !isAnimating ) {
                                                setIsExpanded( true );
                                            }
                                        } }
                                    >
                                        <span>
                                            { __( 'View All', 'reviews-feed' ) } { totalReviews } { __( 'Reviews', 'reviews-feed' ) }
                                        </span>
                                        { renderChevron() }
                                    </a>
                                ) }
                            </div>
                        </div>
                    </div>

                    {/* Powered By Footer */}
                    { showPoweredBy && (
                        <div className="sbr-review-alert__powered-by">
                            <span className="sbr-review-alert__powered-text">
                                { __( 'Powered by', 'reviews-feed' ) }
                            </span>
                            <a
                                href="https://smashballoon.com/reviews-feed/?utm_source=reviews-popup&utm_medium=footer&utm_campaign=powered-by"
                                className="sbr-review-alert__powered-link"
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                            >
                                { SbUtils.printIcon( 'smashballoon-logo-white', 'sbr-review-alert__logo' ) }
                            </a>
                        </div>
                    ) }
                </div>
            </div>
        </>
    );

    // Render expanded content (full review feed view)
    const renderExpandedContent = () => {
        // Show only first N reviews initially, or all if "See all" was clicked
        const reviewsToShow = showAllReviews
            ? displayReviews
            : displayReviews.slice( 0, INITIAL_EXPANDED_REVIEW_COUNT );
        // Show "See all" button if total reviews exceed initial count (use totalReviews for accurate count in preview)
        const hasMoreReviews = totalReviews > INITIAL_EXPANDED_REVIEW_COUNT && !showAllReviews;

        return (
            <div className="sbr-review-alert__expanded">
                {/* Expanded Card */}
                <div className="sbr-review-alert__expanded-card">
                    {/* Header Section */}
                    { ( showExpandedHeading || showExpandedButton ) && (
                        <div className={ `sbr-review-alert__expanded-header ${ showExpandedButton ? 'sbr-review-alert__expanded-header--with-cta' : 'sbr-review-alert__expanded-header--no-cta' }` }>
                            { showExpandedHeading && (
                                <div className={ `sbr-review-alert__expanded-heading ${ showExpandedButton ? 'sbr-review-alert__expanded-heading--with-cta' : '' }` }>
                                    { expandedHeadingText }
                                </div>
                            ) }
                            { showExpandedButton && (
                                <a
                                    href={ expandedButtonUrl }
                                    className={ `sbr-review-alert__expanded-cta ${ expandedButtonIcon ? 'sbr-review-alert__expanded-cta--has-icon' : '' }` }
                                    onClick={ ( e ) => e.preventDefault() }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    { renderButtonIcon( expandedButtonIcon ) }
                                    <span>{ expandedButtonText }</span>
                                </a>
                            ) }
                        </div>
                    ) }

                    {/* Reviews Container - uses event delegation for "See all" button clicks */}
                    <div className={ `sbr-review-alert__expanded-reviews${ !showExpandedHeading && !showExpandedButton ? ' sbr-review-alert__expanded-reviews--no-header' : '' }` } onClick={ handleExpandedReviewsClick }>
                        { reviewsToShow.map( ( review ) => (
                            <div key={ review.id } className="sbr-review-alert__expanded-review">
                                {/* Stars */}
                                { showExpandedStars && (
                                    <div className="sbr-review-alert__expanded-stars">
                                        { renderExpandedStars( review.rating || 5 ) }
                                    </div>
                                ) }

                                {/* Review Title */}
                                { showExpandedTitle && review.text && (
                                    <div className="sbr-review-alert__expanded-review-title">
                                        { ( () => {
                                            const decodedText = SbUtils.decodeHtmlEntities( review.text );
                                            return decodedText.length > 30 ? `${ decodedText.substring( 0, 30 ) }...` : decodedText;
                                        } )() }
                                    </div>
                                ) }

                                {/* Review Text */}
                                { showExpandedContent && (
                                    <p className="sbr-review-alert__expanded-review-text">
                                        { SbUtils.decodeHtmlEntities( review.text ) }
                                    </p>
                                ) }

                                {/* Meta - Author and Date */}
                                { ( showExpandedAuthor || showExpandedDate ) && (
                                    <div className="sbr-review-alert__expanded-review-meta">
                                        { showExpandedAuthor && (
                                            <span className="sbr-review-alert__expanded-review-author">
                                                { SbUtils.decodeHtmlEntities( review.reviewer?.name ) }
                                            </span>
                                        ) }
                                        { showExpandedDate && (
                                            <span className="sbr-review-alert__expanded-review-date">
                                                { review.relativeDate || '2 Feb, 2022' }
                                            </span>
                                        ) }
                                    </div>
                                ) }
                            </div>
                        ) ) }

                        {/* See All Button - only show if there are more reviews */}
                        {/* Uses memoized callback + parent event delegation for robust click handling */}
                        { hasMoreReviews && (
                            <div className="sbr-review-alert__expanded-see-all">
                                <button
                                    type="button"
                                    className="sbr-review-alert__expanded-see-all-btn"
                                    onClick={ handleSeeAllClick }
                                >
                                    <span>{ __( 'See all', 'reviews-feed' ) } { totalReviews } { __( 'Reviews', 'reviews-feed' ) }</span>
                                    { renderChevron() }
                                </button>
                            </div>
                        ) }

                    {/* Powered By - Inside expanded-reviews for proper scrolling/layout */}
                    { showExpandedPoweredBy && (
                        <div className="sbr-review-alert__expanded-powered-wrapper">
                            <a
                                href="https://smashballoon.com/reviews-feed/?utm_source=reviews-popup&utm_medium=footer&utm_campaign=powered-by"
                                className="sbr-review-alert__expanded-powered-pill"
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                            >
                                <span className="sbr-review-alert__expanded-powered-text">
                                    { __( 'Powered by', 'reviews-feed' ) }
                                </span>
                                <span className="sbr-review-alert__expanded-powered-logo">
                                <svg viewBox="0 0 84 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M20.8937 5.66226C20.0529 5.03706 19.2553 4.95083 18.7594 4.95083C18.0911 4.95083 17.4875 5.09096 16.9808 5.60836C16.5497 6.05031 16.3556 6.58928 16.3556 7.22526C16.3556 7.57019 16.4095 8.07682 16.7868 8.47565C17.0671 8.77747 17.4551 8.92838 17.7893 9.04695L18.3821 9.25176C18.587 9.32722 19.0289 9.48891 19.2229 9.65059C19.3738 9.77995 19.4709 9.93086 19.4709 10.168C19.4709 10.4375 19.3523 10.6315 19.2122 10.7501C18.975 10.9549 18.6732 10.998 18.4576 10.998C18.1234 10.998 17.8324 10.9118 17.5521 10.7393C17.3581 10.6207 17.0671 10.3728 16.8838 10.1896L16.0215 11.3753C16.291 11.6448 16.7114 11.9789 17.0779 12.1622C17.5306 12.3885 17.9833 12.464 18.5007 12.464C18.975 12.464 19.902 12.3993 20.538 11.731C20.9153 11.3429 21.174 10.6962 21.174 9.95241C21.174 9.53202 21.0662 9.01462 20.6458 8.61578C20.3655 8.3463 19.9775 8.18461 19.6649 8.06604L19.1259 7.86123C18.6409 7.67799 18.3929 7.61331 18.2205 7.45162C18.1127 7.35461 18.0588 7.22525 18.0588 7.05279C18.0588 6.86954 18.1342 6.70785 18.242 6.60006C18.436 6.38447 18.7163 6.35213 18.9427 6.35213C19.1475 6.35213 19.611 6.38447 20.15 6.86954L20.8937 5.66226Z" fill="currentColor"/>
                                    <path d="M21.8618 12.3023H23.414V9.84462C23.414 9.68293 23.4247 9.14397 23.7158 8.86371C23.8667 8.72358 24.0499 8.66968 24.2548 8.66968C24.4164 8.66968 24.6105 8.70202 24.7722 8.86371C25.0093 9.10085 25.0201 9.47813 25.0201 9.8015V12.3023H26.5723V9.9093C26.5723 9.5967 26.6046 9.21942 26.8094 8.96072C26.9388 8.78825 27.1544 8.66968 27.4131 8.66968C27.6395 8.66968 27.8443 8.76669 27.9736 8.92838C28.1784 9.18709 28.1784 9.61826 28.1784 9.86618V12.3023H29.7306V9.26254C29.7306 8.9176 29.6983 8.27085 29.2348 7.8289C28.9437 7.54863 28.4695 7.37616 27.8874 7.37616C27.5101 7.37616 27.1975 7.44084 26.8633 7.64565C26.5507 7.83967 26.3675 8.06604 26.2489 8.26007C26.1196 7.96903 25.9148 7.73188 25.6668 7.59175C25.3435 7.39772 24.9662 7.37616 24.7614 7.37616C24.3949 7.37616 23.8128 7.45162 23.414 8.05526V7.49474H21.8618V12.3023Z" fill="currentColor"/>
                                    <path d="M34.3094 8.0337C33.8674 7.43006 33.2099 7.33305 32.8003 7.33305C32.132 7.33305 31.5499 7.57019 31.1295 7.99058C30.6875 8.43253 30.3857 9.12241 30.3857 9.93086C30.3857 10.5668 30.5798 11.192 31.0864 11.731C31.6146 12.2915 32.1966 12.464 32.8865 12.464C33.2854 12.464 33.889 12.367 34.3094 11.7202V12.3023H35.8616V7.49474H34.3094V8.0337ZM33.1883 8.66968C33.4686 8.66968 33.8135 8.77747 34.0507 9.00384C34.277 9.21942 34.4064 9.5428 34.4064 9.88774C34.4064 10.2974 34.2339 10.5992 34.0291 10.7932C33.8243 10.998 33.5333 11.1274 33.2207 11.1274C32.8542 11.1274 32.52 10.9764 32.3044 10.7501C32.1643 10.5992 31.9811 10.3189 31.9811 9.88774C31.9811 9.45657 32.1751 9.17631 32.3368 9.01462C32.5416 8.80981 32.8542 8.66968 33.1883 8.66968Z" fill="currentColor"/>
                                    <path d="M40.299 7.74266C39.9109 7.5163 39.4151 7.33305 38.7036 7.33305C38.2617 7.33305 37.6796 7.41928 37.2484 7.81812C36.9682 8.07682 36.7849 8.46487 36.7849 8.92838C36.7849 9.29488 36.9035 9.56436 37.1299 9.8015C37.3347 10.0063 37.6257 10.1572 37.906 10.2435L38.294 10.362C38.5204 10.4267 38.6605 10.4698 38.7683 10.5345C38.9084 10.6207 38.9408 10.7285 38.9408 10.8148C38.9408 10.9333 38.8761 11.0519 38.7791 11.1274C38.639 11.2352 38.391 11.2351 38.294 11.2351C38.0892 11.2351 37.8628 11.192 37.6473 11.0842C37.4856 11.0088 37.27 10.8579 37.1191 10.7285L36.4615 11.7741C37.0867 12.3239 37.7874 12.464 38.4342 12.464C38.9408 12.464 39.5229 12.3885 39.9971 11.9142C40.2127 11.6987 40.493 11.289 40.493 10.6531C40.493 10.2866 40.396 9.99553 40.1157 9.73683C39.8678 9.51046 39.5875 9.40267 39.3181 9.31644L38.9084 9.18708C38.7144 9.12241 38.5527 9.09007 38.4449 9.0254C38.3695 8.98228 38.294 8.9176 38.294 8.80981C38.294 8.73435 38.3371 8.64812 38.391 8.59422C38.488 8.49721 38.6713 8.45409 38.833 8.45409C39.1348 8.45409 39.4474 8.58345 39.6845 8.72358L40.299 7.74266Z" fill="currentColor"/>
                                    <path d="M41.1354 12.3023H42.6876V9.89852C42.6876 9.66137 42.6984 9.19787 43.011 8.89604C43.0972 8.80981 43.2912 8.6589 43.6254 8.6589C43.8518 8.6589 44.0889 8.73435 44.229 8.87449C44.477 9.11163 44.4877 9.49968 44.4877 9.83384V12.3023H46.0399V9.25176C46.0399 8.88526 46.0076 8.2924 45.5657 7.85045C45.1453 7.41928 44.5847 7.36539 44.2075 7.36539C43.8625 7.36539 43.593 7.39772 43.2697 7.57019C43.0864 7.66721 42.8816 7.8289 42.6876 8.06604V4.4442H41.1354V12.3023Z" fill="currentColor"/>
                                    <path d="M49.3027 5.11251V12.3023H52.0083C52.4503 12.3023 53.3126 12.2592 53.9055 11.6879C54.1857 11.4076 54.466 10.9549 54.466 10.2327C54.466 9.5967 54.2396 9.18709 54.0025 8.94994C53.7438 8.69124 53.3557 8.51877 53.0108 8.46487C53.1832 8.38942 53.4312 8.23851 53.6144 7.93669C53.8084 7.62409 53.8516 7.30071 53.8516 7.02045C53.8516 6.70785 53.7977 6.10421 53.3342 5.66226C52.7736 5.13407 51.8897 5.11251 51.5017 5.11251H49.3027ZM50.9412 6.40603H51.1999C51.5017 6.40603 51.8251 6.40603 52.0622 6.58928C52.1916 6.68629 52.3425 6.88032 52.3425 7.19292C52.3425 7.50552 52.2023 7.7211 52.0514 7.8289C51.8143 8.00136 51.437 8.0337 51.2106 8.0337H50.9412V6.40603ZM50.9412 9.26254H51.3939C51.728 9.26254 52.2886 9.26254 52.5796 9.53202C52.6874 9.62904 52.806 9.82306 52.806 10.1141C52.806 10.3728 52.7197 10.5776 52.5688 10.7177C52.267 10.998 51.7604 11.0088 51.34 11.0088H50.9412V9.26254Z" fill="currentColor"/>
                                    <path d="M58.8828 8.0337C58.4408 7.43006 57.7833 7.33305 57.3737 7.33305C56.7054 7.33305 56.1233 7.57019 55.7029 7.99058C55.261 8.43253 54.9591 9.12241 54.9591 9.93086C54.9591 10.5668 55.1532 11.192 55.6598 11.731C56.188 12.2915 56.7701 12.464 57.4599 12.464C57.8588 12.464 58.4624 12.367 58.8828 11.7202V12.3023H60.435V7.49474H58.8828V8.0337ZM57.7617 8.66968C58.042 8.66968 58.3869 8.77747 58.6241 9.00384C58.8505 9.21942 58.9798 9.5428 58.9798 9.88774C58.9798 10.2974 58.8073 10.5992 58.6025 10.7932C58.3977 10.998 58.1067 11.1274 57.7941 11.1274C57.4276 11.1274 57.0934 10.9764 56.8778 10.7501C56.7377 10.5992 56.5545 10.3189 56.5545 9.88774C56.5545 9.45657 56.7485 9.17631 56.9102 9.01462C57.115 8.80981 57.4276 8.66968 57.7617 8.66968Z" fill="currentColor"/>
                                    <path d="M61.3583 4.4442V12.3023H62.9105V4.4442H61.3583Z" fill="currentColor"/>
                                    <path d="M63.8771 4.4442V12.3023H65.4294V4.4442H63.8771Z" fill="currentColor"/>
                                    <path d="M71.764 9.89852C71.764 9.2841 71.5269 8.59422 71.0418 8.10916C70.6107 7.67798 69.8777 7.33305 68.9399 7.33305C68.0021 7.33305 67.2691 7.67798 66.8379 8.10916C66.3528 8.59422 66.1157 9.2841 66.1157 9.89852C66.1157 10.5129 66.3528 11.2028 66.8379 11.6879C67.2691 12.1191 68.0021 12.464 68.9399 12.464C69.8777 12.464 70.6107 12.1191 71.0418 11.6879C71.5269 11.2028 71.764 10.5129 71.764 9.89852ZM68.9399 8.64812C69.2956 8.64812 69.5866 8.76669 69.813 8.99306C70.0394 9.21942 70.1687 9.51046 70.1687 9.89852C70.1687 10.2866 70.0394 10.5776 69.813 10.804C69.5866 11.0303 69.2956 11.1489 68.9506 11.1489C68.541 11.1489 68.2608 10.998 68.0668 10.804C67.8835 10.6207 67.711 10.3405 67.711 9.89852C67.711 9.51046 67.8404 9.21942 68.0668 8.99306C68.2931 8.76669 68.5842 8.64812 68.9399 8.64812Z" fill="currentColor"/>
                                    <path d="M77.8198 9.89852C77.8198 9.2841 77.5827 8.59422 77.0976 8.10916C76.6664 7.67798 75.9334 7.33305 74.9956 7.33305C74.0578 7.33305 73.3248 7.67798 72.8937 8.10916C72.4086 8.59422 72.1715 9.2841 72.1715 9.89852C72.1715 10.5129 72.4086 11.2028 72.8937 11.6879C73.3248 12.1191 74.0578 12.464 74.9956 12.464C75.9334 12.464 76.6664 12.1191 77.0976 11.6879C77.5827 11.2028 77.8198 10.5129 77.8198 9.89852ZM74.9956 8.64812C75.3513 8.64812 75.6424 8.76669 75.8688 8.99306C76.0951 9.21942 76.2245 9.51046 76.2245 9.89852C76.2245 10.2866 76.0951 10.5776 75.8688 10.804C75.6424 11.0303 75.3514 11.1489 75.0064 11.1489C74.5968 11.1489 74.3165 10.998 74.1225 10.804C73.9393 10.6207 73.7668 10.3405 73.7668 9.89852C73.7668 9.51046 73.8961 9.21942 74.1225 8.99306C74.3489 8.76669 74.6399 8.64812 74.9956 8.64812Z" fill="currentColor"/>
                                    <path d="M78.4644 12.3023H80.0166V9.83384C80.0166 9.52124 80.0597 9.17631 80.3184 8.9176C80.437 8.78825 80.6418 8.6589 80.9652 8.6589C81.2454 8.6589 81.4395 8.75591 81.558 8.87449C81.806 9.12241 81.8167 9.52124 81.8167 9.83384V12.3023H83.369V9.26254C83.369 8.87449 83.3366 8.30318 82.8839 7.85045C82.4743 7.44084 81.9245 7.36539 81.5041 7.36539C81.0514 7.36539 80.4909 7.4624 80.0166 8.06604V7.49474H78.4644V12.3023Z" fill="currentColor"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M12.5028 8.07516C12.5028 3.83303 9.7222 0.394081 6.29088 0.394081C2.85955 0.394081 0.0776367 3.83303 0.0776367 8.07516C0.0776367 12.1419 2.62571 15.4601 5.85243 15.7385L5.5093 16.825L7.66086 16.642L6.90597 15.7194C10.0475 15.3381 12.5028 12.0619 12.5028 8.07516Z" fill="#FE544F"/>
                                    <path fillRule="evenodd" clipRule="evenodd" d="M7.77227 3.0945L8.07123 6.17923L11.1689 6.26821L8.9281 8.3517L10.6979 10.9115L7.71637 10.3511L6.81247 13.3327L5.44003 10.6627L2.66888 11.9324L3.7349 9.07008L1.03223 7.70689L3.92536 6.77644L3.12703 3.92636L5.86414 5.48681L7.77227 3.0945Z" fill="white"/>
                                </svg>
                                </span>
                            </a>
                        </div>
                    ) }
                </div>
            </div>

            {/* Close Button */}
            <div className="sbr-review-alert__close-row">
                <button
                    className="sbr-review-alert__close-btn"
                    onClick={ () => setIsExpanded( false ) }
                    aria-label={ __( 'Close', 'reviews-feed' ) }
                >
                    { renderCloseIcon() }
                </button>
            </div>
        </div>
        );
    };

    return (
        <section className="sb-customizer-preview" data-preview-device={ editorActiveDevice?.device || 'desktop' }>
            <section className="sb-preview-wrapper sb-fs sb-tr-2">
                <section className="sb-preview-devices-top sb-fs">
                    <span className="sb-bold sb-text-tiny sb-dark2-text">{ __( 'Preview', 'reviews-feed' ) }</span>
                    <div className="sb-preview-devices-chooser">
                        {
                            devices.map( device => (
                                <button
                                    className="sb-preview-chooser-btn"
                                    data-device={ device }
                                    key={ device }
                                    data-active={ editorActiveDevice?.device === device }
                                    onClick={ () => editorActiveDevice?.setDevice( device ) }
                                >
                                    { SbUtils.printIcon( device ) }
                                </button>
                            ) )
                        }
                    </div>
                </section>

                <section className="sb-popup-preview-area sb-fs">
                    <div className="sbr-review-alert-container">
                        <div
                            className={ `sbr-review-alert sbr-review-alert--preview ${themeClass} ${positionClass} ${poweredByClass} ${popupTypeClass} ${ showExpanded ? 'sbr-review-alert--expanded-state' : '' } ${ isAnimating && !isExpanded && showExpanded ? 'sbr-review-alert--collapsing' : '' }` }
                            style={ customStyle }
                            role={ !showExpanded && canExpand ? 'button' : undefined }
                            tabIndex={ !showExpanded && canExpand ? 0 : undefined }
                            aria-expanded={ showExpanded }
                            onClick={ () => canExpand && !showExpanded && !isAnimating && setIsExpanded( true ) }
                            onKeyDown={ ( e ) => {
                                if ( canExpand && !showExpanded && !isAnimating && ( e.key === 'Enter' || e.key === ' ' ) ) {
                                    e.preventDefault();
                                    setIsExpanded( true );
                                }
                            } }
                        >
                            { isLoading
                                ? renderLoadingSkeleton()
                                : ( displayReviews.length === 0
                                    ? renderEmptyState() // Always show collapsed empty state (has correct styles)
                                    : ( showExpanded
                                        ? renderExpandedContent()
                                        : ( popupType === 'aggregate' ? renderAggregateContent() : renderPopupContent() )
                                    )
                                )
                            }
                        </div>
                    </div>
                </section>
            </section>
        </section>
    );
};

export default ReviewAlertPreview;
